import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";

import {
  FolderKanban,
  LayoutGrid,
  Boxes,
  X,
  Eye,
  ShoppingCart,
  PackageCheck,
  CheckCircle,
} from "lucide-react";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";
import AddInventorySubCategoryModal from "./AddInventorySubCategoryModal";
import AddInventoryCategoryModal from "./AddInventoryCategoryModal";
import AddGlobalInventoryItemModal from "./AddGlobalInventoryItemModal";
import NoIndex from "../../../components/NoIndex";
import BarcodeModal from "./BarcodeModal";
import { toast } from "react-toastify";
import placeholderImage from "../../../images/Place Holder 3 1.webp";

const SkeletonCard = () => {
  return (
    <div className="bg-white border rounded-xl p-3">
      {/* Image */}
      <div className="h-24 shimmer mb-3"></div>

      {/* Title */}
      <div className="h-3 shimmer w-3/4 mb-2"></div>

      {/* Subtitle */}
      <div className="h-2 shimmer w-1/2 mb-2"></div>

      {/* Price */}
      <div className="h-3 shimmer w-1/3"></div>

      {/* Button */}
      <div className="h-8 shimmer mt-3"></div>
    </div>
  );
};

const GlobalInventory = () => {

const {
  allInventoryCategories,
  loadingAllCategories,
  inventoryItems,
  loadingItems,
  fetchInventoryAllCategories,
  addItemsToInventory,
  setSubCategories,
  addInventorySubCategory,
  variants,
  variantsLoading,
  fetchAllInventoryVariants,
  updateInventoryVariant,
} = useGlobalInventory();

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [subCategories, setLocalSubCategories] = useState([]);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchWord, setSearchWord] = useState("");
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
const [localInventoryItems, setLocalInventoryItems] = useState([]);
const [visibleCount, setVisibleCount] = useState(100);
const throttleTimerRef = useRef(null);

const selectedCategoryName =
  allInventoryCategories.find((c) => c.category_id === selectedCategoryId)
    ?.category_name || "";

  const normalizeCategories = (categories = []) => {
  return categories.map((cat) => ({
    id: cat.category_id,
    category: cat.category_name,
    categoryCode: cat.category_code,
    description: cat.description,

    subCategories: (cat.sub_category_list || []).map((sub) => ({
      id: sub.sub_category_id,
      subCategory: sub.sub_category_name,
      subCategoryCode: sub.sub_category_code,
      subCategoryDescription: sub.sub_category_description,
      subCategoryImage: sub.item_list?.[0]?.image_url || "",

      products: (sub.item_list || []).map((item) => ({
        id: item.item_id,
        itemId: item.item_id,
        itemCode: item.item_code,
        productImage: item.image_url || "",
        productName: item.item_name || "Item",
        category: cat.category_name || "",
        subCategory: sub.sub_category_name || "",
        weight: item.sku || item.hsn_code || "",
        hsn_code: item.hsn_code || "",
        price: item.selling_price || item.mrp || "0",
        mrp: item.mrp || "0",
        purchasePrice: item.purchase_price || "0",
        sellingPrice: item.selling_price || "0",
        gstPercentage: item.gst_percentage || "0",
        barcode: item.barcode || "",
        brandId: item.brand_id || "",
        brandName: item.brand_name || "",
        brandCode: item.brand_code || "",
        description: item.item_description || "",
      })),
    })),
  }));
};

const flattenProductsBySubCategory = (sub) => {
  return (sub?.products || []).map((item) => ({
    ...item,
    category: item.category || "",
    subCategory: item.subCategory || sub?.subCategory || "",
  }));
};

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

 const loadCategories = async () => {
  setInitialLoading(true);

  await fetchAllInventoryVariants();

  const apiData = await fetchInventoryAllCategories();
  const data = normalizeCategories(apiData);

  if (data?.length) {
    const first = data[0];

    setSelectedCategoryId(first.id);
    setLocalSubCategories(first.subCategories || []);
    setSubCategories(first.subCategories || []);

    const firstSub = first.subCategories?.[0];

    if (firstSub) {
      setSelectedSubCategoryId(firstSub.id);

      const products = flattenProductsBySubCategory(firstSub);

      // local product list set karne ke liye state add karenge next step me
      setLocalInventoryItems(products);
    }
  }

  setInitialLoading(false);
};

  const handleOpenBarcodeModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const refreshInventoryAfterSubCategoryAdd = async () => {
 const apiData = await fetchInventoryAllCategories(true);
  const data = normalizeCategories(apiData);

  if (data?.length) {
    const selectedCategory = data.find((c) => c.id === selectedCategoryId);

    const subs = selectedCategory?.subCategories || [];

    setLocalSubCategories(subs);
    setSubCategories(subs);

    const selectedSub = subs.find((s) => s.id === selectedSubCategoryId);

    if (selectedSub) {
      setLocalInventoryItems(flattenProductsBySubCategory(selectedSub));
    }
  }
};

  // CATEGORY CLICK
 const handleCategorySelect = (cat) => {
  setSelectedCategoryId(cat.id);

  const subs = cat.subCategories || [];

  setLocalSubCategories(subs);
  setSubCategories(subs);

  if (subs.length > 0) {
    const firstSub = subs[0];

    setSelectedSubCategoryId(firstSub.id);
    setLocalInventoryItems(flattenProductsBySubCategory(firstSub));
  } else {
    setSelectedSubCategoryId(null);
    setLocalInventoryItems([]);
  }
};

  // SUBCATEGORY CLICK
const handleSubCategoryClick = (sub) => {
  setSelectedSubCategoryId(sub.id);
  setLocalInventoryItems(flattenProductsBySubCategory(sub));
};

  // SELECT ITEM
  const toggleItemSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // ADD INVENTORY
  const handleAddInventory = async () => {
    if (!selectedItems.length) return;

    const res = await addItemsToInventory(selectedItems);

    if (res?.responseCode === "00") {
      setSelectedItems([]);
      toast.success("Items added successfully");
    }
  };

  // SEARCH


const normalizedCategories = useMemo(() => {
  return normalizeCategories(allInventoryCategories);
}, [allInventoryCategories]);

const items = useMemo(() => {
  const word = searchWord.trim().toLowerCase();

  if (word.length < 3) {
    return localInventoryItems;
  }

  return localInventoryItems.filter((item) => {
    return (
      item.productName?.toLowerCase().includes(word) ||
      item.itemCode?.toLowerCase().includes(word) ||
      item.barcode?.toLowerCase().includes(word) ||
      item.hsn_code?.toLowerCase().includes(word) ||
      item.category?.toLowerCase().includes(word) ||
      item.subCategory?.toLowerCase().includes(word) ||
      item.brandName?.toLowerCase().includes(word)
    );
  });
}, [searchWord, localInventoryItems]);

const visibleItems = items.slice(0, visibleCount);

const loadMoreItems = useCallback(() => {
  setVisibleCount((prev) => {
    if (prev >= items.length) {
      return prev;
    }

    return Math.min(prev + 100, items.length);
  });
}, [items.length]);

useEffect(() => {
  const handleScroll = (event) => {
    // 300ms ke andar function dobara execute nahi hoga
    if (throttleTimerRef.current) {
      return;
    }

    throttleTimerRef.current = setTimeout(() => {
      const target = event.target;

      let scrollTop = 0;
      let clientHeight = 0;
      let scrollHeight = 0;

      const isDocumentScroll =
        target === document ||
        target === document.documentElement ||
        target === document.body;

      if (isDocumentScroll) {
        scrollTop =
          window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0;

        clientHeight = window.innerHeight;
        scrollHeight = document.documentElement.scrollHeight;
      } else {
        scrollTop = target.scrollTop;
        clientHeight = target.clientHeight;
        scrollHeight = target.scrollHeight;
      }

      const distanceFromBottom =
        scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom <= 500) {
        loadMoreItems();
      }

      throttleTimerRef.current = null;
    }, 300);
  };

  document.addEventListener("scroll", handleScroll, true);

  return () => {
    document.removeEventListener("scroll", handleScroll, true);

    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
  };
}, [loadMoreItems]);

useEffect(() => {
  setVisibleCount(100);
}, [selectedCategoryId, selectedSubCategoryId, searchWord]);

useEffect(() => {
  fetchAllInventoryVariants();
}, []);

if (initialLoading || loadingAllCategories || loadingItems) {

    return (
      <>

      <div className="p-6 min-h-screen space-y-6 montserrat-font-1">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <div className="h-6 w-40 shimmer"></div>
          <div className="h-10 w-64 shimmer rounded-lg"></div>
        </div>

        {/* Categories Skeleton */}
        <div className="bg-white border rounded-xl p-4 space-y-3">
          <div className="h-4 w-32 shimmer"></div>

          <div className="flex gap-2 flex-wrap">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-6 w-20 shimmer rounded-full"></div>
            ))}
          </div>
        </div>

        {/* Subcategories Skeleton */}
        <div className="bg-white border rounded-xl p-4">
          <div className="h-4 w-32 shimmer mb-3"></div>

          <div className="grid grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 shimmer rounded-lg"></div>
            ))}
          </div>
        </div>

        {/* Products Skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-5">
          {Array.from({ length: 12 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
      </>
    );
  }

  return (
    
    <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
    
     <NoIndex />



  {/* HEADER */}
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-semibold text-gray-800 montserrat-font-1">
        Global Inventory
      </h2>

      <div className="relative w-80">
        <input
          type="text"
          placeholder="Search products..."
          className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
        />

        {searchWord && (
          <button
            onClick={() => setSearchWord("")}
            className="absolute right-3 top-2 text-gray-500"
          >
            ✕
          </button>
        )}
      </div>
    </div>

<div className="flex-1 min-h-0 overflow-y-auto">
      {/* CATEGORIES */}
    <div className="bg-white border rounded-xl p-4 shadow-sm mb-4">
        <div className="flex justify-between  items-center mb-3">
          <div className="flex items-center gap-2 font-semibold text-gray-700 montserrat-font-1">
            <FolderKanban size={16} />
            Categories
          </div>

          <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white text-sm px-4 py-1.5 rounded-full montserrat-font-1"
          >
            + Add Category
          </button>
        </div>

        <div className="flex flex-wrap gap-2 montserrat-font-1">
    {normalizedCategories.map((cat) => (
  <button
    key={cat.id}
    onClick={() => handleCategorySelect(cat)}
    className={`flex items-center gap-2 border rounded-full px-3 py-1 text-xs transition
      ${
        selectedCategoryId === cat.id
          ? "border-green-500 bg-green-50"
          : "border-gray-200 bg-white"
      }`}
  >
    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
    {cat.category}
  </button>
))}
        </div>
      </div>

      {/* SUBCATEGORIES */}
      {subCategories.length > 0 && (
        <div className="bg-white border rounded-xl p-4 shadow-sm mb-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2 font-semibold text-gray-700">
              <LayoutGrid size={16} />
              Subcategories
            </div>

            <button
              onClick={() => setShowSubCategoryModal(true)}
              className="bg-green-500 hover:bg-green-600 text-white text-sm px-4 py-1.5 rounded-full montserrat-font-1"
            >
              + Add Subcategory
            </button>
          </div>

          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-4">
            {subCategories.map((sub) => (
              <div
                key={sub.id}
                onClick={() => handleSubCategoryClick(sub)}
                className={`min-w-[120px] flex-shrink-0 cursor-pointer border rounded-lg p-3 text-center transition
        ${
          selectedSubCategoryId === sub.id
            ? "border-green-500 bg-green-50"
            : "border-gray-200"
        }`}
              >
    <img
  src={sub.subCategoryImage || placeholderImage}
  alt={sub.subCategory || "Subcategory"}
  loading="lazy"
  decoding="async"
  onError={(e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = placeholderImage;
}}
  className="w-16 h-16 object-contain mx-auto"
/>

                <p className="text-xs mt-2 font-medium text-gray-700">
                  {sub.subCategory}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PRODUCTS */}
    <div className="mt-2">
        <div className="flex justify-between items-center mb-3 montserrat-font-1">
          <div className="flex items-center gap-2 font-semibold">
            <Boxes size={18} />
            <h3>Products</h3>
          </div>

          <button
            onClick={() => setShowAddItemModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white text-sm px-4 py-1.5 rounded-full"
          >
            + Add Item
          </button>
        </div>

<div>
{loadingItems ? (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-5">
    {Array.from({ length: 12 }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
) : (
  <>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-5 montserrat-font-1">
      {visibleItems.map((item) => (
        <InventoryCard
          key={item.id}
          item={item}
          selected={selectedItems.includes(item.id)}
          onSelect={toggleItemSelection}
          onBarcodeClick={handleOpenBarcodeModal}
        />
      ))}
    </div>

   {visibleCount < items.length && (
  <div className="flex justify-center mt-6 text-sm text-gray-400">
    Scroll down to view more products
  </div>
)}

  </>
)}
</div>
      </div>
</div>
      {/* FLOATING PANEL */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-6 right-6 bg-white shadow-2xl rounded-xl w-80 p-4 border z-50 montserrat-font-1">
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-sm">
              {selectedItems.length} Selected
            </h4>

            <button
              onClick={() => setSelectedItems([])}
              className="text-red-500"
            >
              <X size={16} />
            </button>
          </div>

          {/* Selected Items */}
          <div className="max-h-40 overflow-y-auto space-y-2 mb-3">
            {items
              .filter((p) => selectedItems.includes(p.id))
              .map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 bg-gray-50 p-2 rounded"
                >
   <img
  src={p.productImage || placeholderImage}
  loading="lazy"
  decoding="async"
 onError={(e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = placeholderImage;
}}
  className="w-6 h-6 object-contain"
/>

                  <span className="text-xs flex-1 truncate">
                    {p.productName}
                  </span>

                  <button
                    onClick={() => toggleItemSelection(p.id)}
                    className="text-red-500"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
          </div>

          {/* Add Button */}
          <button
            onClick={handleAddInventory}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm"
          >
            Add to Main Inventory
          </button>
        </div>
      )}

      <AddInventorySubCategoryModal
        show={showSubCategoryModal}
        onClose={() => setShowSubCategoryModal(false)}
        selectedCategoryName={selectedCategoryName}
        onSuccess={refreshInventoryAfterSubCategoryAdd}
      />

      <AddInventoryCategoryModal
        show={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSuccess={loadCategories}
      />

      <AddGlobalInventoryItemModal
        show={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        selectedCategory={selectedCategoryName}
        selectedSubCategory={
          subCategories.find((s) => s.id === selectedSubCategoryId)?.subCategory
        }
        variants={variants}
      masterCategories={normalizedCategories}
      />

      <BarcodeModal
        open={showModal}
        onClose={() => setShowModal(false)}
        item={selectedItem}
      />
    </div>
  );
};

/* ---------------- INVENTORY CARD ---------------- */

const InventoryCard = ({ item, selected, onSelect, onBarcodeClick }) => {
  const {
  id,
  productImage,
  productName,
  category,
  subCategory,
  weight,
  price,
  hsn_code,
  barcode,
} = item;

  return (
  <div
    onClick={() => onSelect(id)}
    className={`relative bg-white border rounded-xl p-3 hover:shadow-md transition cursor-pointer flex flex-col montserrat-font-1 h-[345px]
  ${selected ? "border-green-500 ring-2 ring-green-200" : ""}`}
  >
      {selected && (
        <div className="absolute top-2 right-2 text-green-600 bg-white rounded-full">
          <CheckCircle size={18} />
        </div>
      )}
      {/* PRODUCT IMAGE */}
     <div className="h-[105px] w-full flex items-center justify-center overflow-hidden">
<img
  src={productImage || placeholderImage}
  alt={productName || "Product"}
  loading="lazy"
  decoding="async"
  onError={(e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = placeholderImage;
}}
  className="max-h-[100px] w-full object-contain"
/>
</div>

      {/* PRODUCT INFO */}
 <div className="text-left mt-2 montserrat-font-1 flex-1">
  <h4
    title={productName}
    className="text-[11px] font-semibold leading-tight min-h-[32px] line-clamp-2"
  >
    {productName}
  </h4>

  <p
    title={`${category} | ${subCategory}`}
    className="text-[10px] text-gray-400 montserrat-font-2 mt-1 overflow-hidden whitespace-nowrap text-ellipsis"
  >
    {category} | {subCategory}
  </p>

  <div className="mt-2 space-y-1.5">
    <div className="flex items-start gap-1 text-[10px]">
      <span className="font-semibold text-gray-700 shrink-0">
        SKU:
      </span>

      <span
        title={weight || "N/A"}
        className="text-gray-500 break-all line-clamp-1"
      >
        {weight || "N/A"}
      </span>
    </div>

    <div className="flex items-start gap-1 text-[10px]">
      <span className="font-semibold text-gray-700 shrink-0">
        HSN:
      </span>

      <span
        title={hsn_code || "N/A"}
        className="text-gray-500 break-all line-clamp-1"
      >
        {hsn_code || "N/A"}
      </span>
    </div>

    <div className="flex items-start gap-1 text-[10px]">
      <span className="font-semibold text-gray-700 shrink-0">
        Barcode:
      </span>

      <span
        title={barcode || "N/A"}
        className="text-gray-500 break-all line-clamp-2"
      >
        {barcode || "N/A"}
      </span>
    </div>
  </div>
</div>

      {/* INVENTORY BUTTON */}
      <button
    className="mt-2 w-full bg-green-500 hover:bg-green-600 text-white text-sm py-2 rounded-md shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}
      >
        + Inventory
      </button>

      {/* ICON ROW */}
    <div className="flex justify-center gap-6 mt-3 text-gray-500 shrink-0">
      <div 
          className="group relative cursor-pointer" 
          title="View Barcode"
          onClick={(e) => {
            e.stopPropagation();
            onBarcodeClick(item); 
          }}
        >
          <Eye
            size={18}
            className="transition duration-200 group-hover:text-blue-500 group-hover:scale-110"
          />
        </div>

        <div className="group relative cursor-pointer" title="Add to Cart">
          <ShoppingCart
            size={18}
            className="transition duration-200 group-hover:text-green-500 group-hover:scale-110"
          />
        </div>

        <div
          className="group relative cursor-pointer"
          title="Add Barcode"
          onClick={(e) => {
            e.stopPropagation();
            onBarcodeClick(item);
          }}
        >
          <PackageCheck
            size={18}
            className="transition duration-200 group-hover:text-purple-500 group-hover:scale-110"
          />
        </div>
      </div>
    </div>
  );
};

export default GlobalInventory;
